import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

const TermsOfService: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Daily Stories</title>
        <meta name="description" content="Terms of Service for Daily Stories. Read our terms and conditions for using the app and subscription service." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 300, mb: 2 }}>
              Terms of Service
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 4 }}>
              Last Updated: December 2024
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                1. ACCEPTANCE OF TERMS
              </Typography>
              <Typography variant="body1" paragraph>
                By downloading, accessing, or using Daily Stories ("the App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms constitute a legally binding agreement between you and Plotcamp ("Company", "we", "us", or "our").
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                2. AGE REQUIREMENT AND ELIGIBILITY
              </Typography>
              <Typography variant="body1" paragraph>
                You must be at least 18 years of age to use this App. By using the App, you represent and warrant that:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>You are at least 18 years old</li>
                <li>You have the legal capacity to enter into this agreement</li>
                <li>You are not prohibited from using the App under applicable laws</li>
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                The App contains adult content that is not suitable for minors. We reserve the right to verify your age and terminate access if you are found to be under 18.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                3. SUBSCRIPTION TERMS
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.1 Subscription Plans
              </Typography>
              <Typography variant="body1" paragraph>
                The App offers premium subscription access to content. Current subscription options include:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Monthly subscription at $4.99/month (or local equivalent)</li>
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.2 Free Trial
              </Typography>
              <Typography variant="body1" paragraph>
                We may offer free trial periods. If you do not cancel before the trial ends, you will be automatically charged for the subscription.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.3 Billing
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Payment will be charged to your Apple App Store or Google Play account at confirmation of purchase</li>
                <li>Your subscription will automatically renew unless auto-renew is turned off at least 24 hours before the end of the current billing period</li>
                <li>Your account will be charged for renewal within 24 hours prior to the end of the current period</li>
                <li>The renewal charge will be at the same price as the original subscription</li>
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.4 Cancellation
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>You may cancel your subscription at any time through your Apple App Store or Google Play account settings</li>
                <li>Cancellation takes effect at the end of the current billing period</li>
                <li>No refunds will be provided for partial subscription periods</li>
                <li>You will retain access to premium content until the end of your paid period</li>
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.5 Price Changes
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to change subscription prices. Price changes will take effect at the start of the next subscription period following the date of the price change. Your continued use after the price change constitutes acceptance.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                3.6 Refunds
              </Typography>
              <Typography variant="body1" paragraph>
                All purchases are final. Refund requests must be directed to Apple App Store or Google Play, as applicable, in accordance with their refund policies.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                4. CONTENT AND LICENSE
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                4.1 Content Description
              </Typography>
              <Typography variant="body1" paragraph>
                The App provides access to fictional erotic stories and related content for adult entertainment purposes.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                4.2 License Grant
              </Typography>
              <Typography variant="body1" paragraph>
                We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the App and its content for personal, non-commercial use only.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                4.3 Content Ownership
              </Typography>
              <Typography variant="body1" paragraph>
                All stories, text, graphics, logos, and other content are owned by Plotcamp or our licensors and are protected by copyright and other intellectual property laws.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                4.4 Restrictions
              </Typography>
              <Typography variant="body1" paragraph>
                You may not:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Copy, reproduce, distribute, or publicly display any content</li>
                <li>Modify, adapt, or create derivative works from the content</li>
                <li>Use the content for any commercial purpose</li>
                <li>Remove any copyright or proprietary notices</li>
                <li>Use automated systems to access or scrape content</li>
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                4.5 Fictional Nature
              </Typography>
              <Typography variant="body1" paragraph>
                All stories and characters are entirely fictional. Any resemblance to real persons, living or dead, or actual events is purely coincidental.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                5. USER CONDUCT
              </Typography>
              <Typography variant="body1" paragraph>
                You agree not to:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Use the App if you are under 18 years of age</li>
                <li>Share your account credentials with others</li>
                <li>Attempt to bypass, disable, or circumvent any security or access restrictions</li>
                <li>Use the App for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt the App's operation</li>
                <li>Attempt to reverse engineer the App</li>
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                6. ACCOUNT SECURITY
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for maintaining the confidentiality of your account. You agree to notify us immediately of any unauthorized use of your account.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                7. INTELLECTUAL PROPERTY
              </Typography>
              <Typography variant="body1" paragraph>
                All intellectual property rights in the App and its content belong to Plotcamp or our licensors. Our trademarks, service marks, and logos may not be used without our prior written consent.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                8. DISCLAIMER OF WARRANTIES
              </Typography>
              <Typography variant="body1" paragraph>
                THE APP AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </Typography>
              <Typography variant="body1" paragraph>
                WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                9. LIMITATION OF LIABILITY
              </Typography>
              <Typography variant="body1" paragraph>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PLOTCAMP AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE APP.
              </Typography>
              <Typography variant="body1" paragraph>
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE APP IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                10. INDEMNIFICATION
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to indemnify and hold harmless Plotcamp and its affiliates from any claims, damages, losses, or expenses arising from your use of the App or violation of these Terms.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                11. TERMINATION
              </Typography>
              <Typography variant="body1" paragraph>
                We may terminate or suspend your access to the App immediately, without notice, for any reason, including breach of these Terms. Upon termination, your license to use the App ceases immediately.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                12. GOVERNING LAW
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms shall be governed by and construed in accordance with the laws of the Netherlands, without regard to conflict of law principles.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                13. DISPUTE RESOLUTION
              </Typography>
              <Typography variant="body1" paragraph>
                Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of the Netherlands.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                14. SEVERABILITY
              </Typography>
              <Typography variant="body1" paragraph>
                If any provision of these Terms is found invalid or unenforceable, the remaining provisions shall continue in full force and effect.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                15. ENTIRE AGREEMENT
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Plotcamp regarding the App.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                16. CHANGES TO TERMS
              </Typography>
              <Typography variant="body1" paragraph>
                We may update these Terms at any time. We will notify you of material changes through the App. Your continued use after changes constitutes acceptance.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                17. CONTACT
              </Typography>
              <Typography variant="body1" paragraph>
                For questions about these Terms, contact us at:
              </Typography>
              <Typography variant="body1">
                Email: <a href="mailto:cem@plotcamp.com" style={{ color: 'inherit' }}>cem@plotcamp.com</a>
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ textAlign: 'center', pt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              © 2024-2025 Plotcamp. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfService;
